import { TextInput } from 'react-native'
import React, { useRef, useCallback, useMemo } from 'react'
import { XStack, Input, styled } from '@red-ui/components'
import { DatePart } from './types'

type DateSelectsProps = {
  isUSUser?: boolean
  dateOfBirth?: {
    day: string
    month: string
    year: string
  }
  handleDateChange: (field: DatePart, value: string) => void
}

type DateFieldConfig = {
  field: DatePart
  placeholder: string
  maxLength: number
}

const DATE_FIELDS: DateFieldConfig[] = [
  { field: 'day', placeholder: 'DD', maxLength: 2 },
  { field: 'month', placeholder: 'MM', maxLength: 2 },
  { field: 'year', placeholder: 'YYYY', maxLength: 4 },
]

export const StyledInput = styled(Input, {
  height: 40,
  borderWidth: 2,
  borderRadius: 0,
  borderTopWidth: 0,
  borderLeftWidth: 0,
  borderRightWidth: 0,
  borderColor: '$black9',
  outlineWidth: 0,
  focusStyle: {
    outlineWidth: 0,
    borderColor: '$primary',
  },
  padding: 10,
  fontSize: 16,
})

export const DateInput = React.memo(({ isUSUser, dateOfBirth = { day: '', month: '', year: '' }, handleDateChange }: DateSelectsProps) => {
  const dayInputRef = useRef<TextInput>(null)
  const monthInputRef = useRef<TextInput>(null)
  const yearInputRef = useRef<TextInput>(null)

  const getRefForField = (field: DatePart): React.RefObject<TextInput> => {
    switch (field) {
      case 'day':
        return dayInputRef
      case 'month':
        return monthInputRef
      case 'year':
        return yearInputRef
      default:
        return dayInputRef
    }
  }

  const getNextInputRef = useCallback(
    (currentField: DatePart): React.RefObject<TextInput> => {
      if (isUSUser) {
        return currentField === 'month' ? dayInputRef : yearInputRef
      }
      return currentField === 'day' ? monthInputRef : yearInputRef
    },
    [isUSUser]
  )

  const orderedFields = useMemo(() => {
    if (isUSUser) {
      return [DATE_FIELDS[1], DATE_FIELDS[0], DATE_FIELDS[2]]
    }
    return DATE_FIELDS
  }, [isUSUser])

  const handleInputChange = useCallback(
    (field: DatePart, text: string) => {
      const numericText = text.replace(/\D/g, '')
      const previousValue = dateOfBirth[field]
      handleDateChange(field, numericText)

      if ((field === 'day' || field === 'month') && numericText.length === 2 && previousValue.length < 2) {
        const nextRef = getNextInputRef(field)
        nextRef?.current?.focus()
      }
    },
    [dateOfBirth, handleDateChange, getNextInputRef]
  )

  return (
    <XStack gap="$2" flex={1}>
      {orderedFields.map(({ field, placeholder, maxLength }) => {
        const ref = getRefForField(field)
        return (
          <StyledInput
            key={field}
            ref={ref}
            value={dateOfBirth[field]}
            width={field === 'year' ? 60 : 45}
            placeholder={placeholder}
            maxLength={maxLength}
            inputMode="numeric"
            onChange={(e) => handleInputChange(field, e.nativeEvent.text)}
            aria-label={`Date of birth ${field}`}
          />
        )
      })}
    </XStack>
  )
})

DateInput.displayName = 'DateInput'
